import type { ComponentProps, PropsWithChildren } from 'react'
import { Text, Platform, SectionListData } from 'react-native'
import Animated from 'react-native-reanimated'
import { BottomSheetSectionList } from '@gorhom/bottom-sheet'
import { CardActivityApi } from '../../stores'
import { mesaColorNames, spacing, typography } from '@/src/common/theme'
import ReturnIcon from '@/src/assets/icons/ReturnIcon'
import { PurchaseIcon } from '@/src/assets/icons/PurchaseIcon'
import { Routes } from '@/src/common/enums/routes'
import { CardPaymentsE2E } from '@/src/experiences/card-payments/e2e'
import { CardActivityTypes } from '@acre-rewards/lib'
import { capitalize, lowerCase, upperFirst } from 'lodash'
import { PaymentIcon } from '@/src/assets/icons/PaymentIcon'
import { Header } from './header'
import { Empty } from './empty'
import { BottomSheet, type BottomSheetProps } from './bottom-sheet'
import { Item } from './item'
import { AutoPayIcon } from '@/src/assets/icons/AutoPayIcon'
import { formatToTextMonth } from '@/src/common/utils/date'
import { renderPaymentScheduleAmount } from '@/src/experiences/card-activity/components/PaymentScheduleRow'
import { Skeleton } from '@/src/design-system/Skeleton'
import { formatCentsAsDollars } from '@/src/common/utils/general'

const FeedBottomSheetSectionList = BottomSheetSectionList<ItemProps, SectionListData<ItemProps>>

export interface CardActivityFeedProps extends PropsWithChildren, BottomSheetProps {
  onRefresh?: () => void
}

type CardActivity =
  (typeof CardActivityApi)['endpoints']['GET /card/activity-feed/:cardAccountId']['Types']['ResultType']['data'][number]

type PaymentSchedule =
  (typeof CardActivityApi)['endpoints']['GET /card/:cardAccountId/payments/schedules']['Types']['ResultType'][number]

interface ItemProps extends ComponentProps<typeof Item> {
  id: string
}

const transformToPropsByActivityType = ((activity) => ({
  date: formatToTextMonth(new Date(activity.sourceCreatedAt + 'Z'), {}),
  ...(() => {
    switch (activity.type) {
      case 'TRANSACTION': {
        const isPendingTransaction = activity.transaction.status === 'PENDING'
        const isRefundTransaction = activity.transaction.type === 'REFUND'

        const props = {
          id: activity.transaction.id,
          icon: isRefundTransaction ? <ReturnIcon /> : <PurchaseIcon />,
          title: activity.transaction.merchantName,
          status: isPendingTransaction ? 'Pending' : undefined,
          amount: formatCentsAsDollars(
            isRefundTransaction
              ? -activity.transaction.amountCents
              : activity.transaction.amountCents,
          ),
          amountColor:
            isRefundTransaction && isPendingTransaction
              ? mesaColorNames['Mesa Third #E78A43']
              : mesaColorNames['Primary/Brand #1C1C1C'],
          points: activity.transaction.pointsEarn.reduce((total, { points }) => total + points, 0),
          ...(isPendingTransaction
            ? {}
            : {
                linkProps: {
                  href: {
                    pathname: Routes.TransactionEventDetails.route,
                    params: { id: activity.transaction.id },
                  },
                },
              }),
        } as const satisfies ItemProps

        return props
      }
      case 'CARD_ACCOUNT_TRANSFER': {
        const props = {
          id: activity.transfer.id,
          icon: <PaymentIcon />,
          title: 'Payment',
          status:
            activity.status === 'PENDING'
              ? 'Pending'
              : activity.status === 'FAILED'
                ? 'Failed'
                : undefined,
          amount: formatCentsAsDollars(activity.transfer.amountCents),
          amountColor: mesaColorNames['Greyscale/500 #6E7375'],
        } as const satisfies ItemProps

        return props
      }
      case 'STATEMENT_CREDIT': {
        const isPendingTransfer = activity.status === 'PENDING'
        // KING: Confirm how we plan on delineating isPointRedemption vs other
        const isPointsRedemption = activity.statementCredit.description === 'statement_credit'

        const props = {
          id: activity.statementCredit.id,
          icon: <PaymentIcon />,
          title: isPointsRedemption ? 'Statement Credit' : activity.statementCredit.name,
          status: isPendingTransfer ? 'Pending' : undefined,
          amount: formatCentsAsDollars(-activity.statementCredit.amountCents),
          amountColor: isPendingTransfer
            ? mesaColorNames['Mesa Third #E78A43']
            : mesaColorNames['Wide/600 #00A498'],
        } as const satisfies ItemProps

        return props
      }
      case 'FEE': {
        return {
          id: activity.fee.id,
          icon: <PaymentIcon />,
          title: activity.fee.description,
          amount: formatCentsAsDollars(activity.fee.amountCents),
          amountColor: mesaColorNames['Mesa Third #E78A43'],
        } as const satisfies ItemProps
      }
      default: {
        activity satisfies never
        throw new Error('Unhandled Card Activity Type')
      }
    }
  })(),
})) satisfies (activity: CardActivity) => ItemProps

const transformToPropsByPaymentSchedulesTypes = ((payment) => ({
  id: payment.id,
  date: payment.processDateTime
    ? formatToTextMonth(new Date(payment.processDateTime), {})
    : undefined,
  status: capitalize(payment.status),
  amount: renderPaymentScheduleAmount(payment),
  amountColor: mesaColorNames['Greyscale/500 #6E7375'],
  ...(() => {
    switch (payment.type) {
      case 'single':
        return {
          icon: <PaymentIcon />,
          title: 'One-time Payment',
        }
      case 'recurring':
        return {
          icon: <AutoPayIcon />,
          title: 'Auto-pay',
        }
    }
  })(),
})) satisfies (payment: PaymentSchedule) => ItemProps

type CardAccountId =
  (typeof CardActivityApi)['endpoints']['GET /card/activity-feed/:cardAccountId']['Types']['QueryArg']['params']['cardAccountId']

export function Feed(props: CardActivityFeedProps & { cardAccountId?: CardAccountId }) {
  return props.cardAccountId ? (
    <FeedWithCardAccount {...props} cardAccountId={props.cardAccountId} />
  ) : (
    <FeedWithoutCardAccount {...props} />
  )
}

/** 6 is approximately as tall as container would be on an average sized screen without being scrollable */
function generateStubCardActivities(amount = 6) {
  return Array(amount)
    .fill(undefined)
    .map((_, i) => <Item key={`stub-${i}`} loading style={{ minHeight: 60 }} />)
}

function FeedWithCardAccount(
  props: ComponentProps<typeof Feed> & { cardAccountId: CardAccountId },
) {
  const { cardActivityFeedBottomSheetHeightStore, onRefresh, cardAccountId } = props

  const pendingActivitiesQuery = CardActivityApi.endpoints[
    'GET /card/activity-feed/:cardAccountId'
  ].useQuery({ params: { cardAccountId }, query: { status: 'PENDING', limit: 88 } })

  const finalizedActivitiesQuery =
    CardActivityApi.endpoints.getFinalizedCardActivitiesByAccountIdInfinite.useInfiniteQuery({
      cardAccountId,
    })

  const paymentSchedulesQuery = CardActivityApi.endpoints[
    'GET /card/:cardAccountId/payments/schedules'
  ].useQuery({ params: { cardAccountId } })

  const pendingActivitiesLoading =
    pendingActivitiesQuery.isUninitialized || pendingActivitiesQuery.isLoading

  const pendingActivities = pendingActivitiesLoading
    ? []
    : pendingActivitiesQuery.data
      ? pendingActivitiesQuery.data.data
      : []

  const finalizedActivitiesLoading =
    finalizedActivitiesQuery.isUninitialized || finalizedActivitiesQuery.isLoading

  const finalizedActivities = finalizedActivitiesLoading
    ? []
    : finalizedActivitiesQuery.data
      ? finalizedActivitiesQuery.data.pages.flatMap((page) => page.data)
      : []

  const paymentSchedulesLoading =
    paymentSchedulesQuery.isUninitialized || paymentSchedulesQuery.isLoading

  const paymentSchedules = paymentSchedulesLoading
    ? []
    : paymentSchedulesQuery.data
      ? paymentSchedulesQuery.data
      : []

  const generateCardActivitiesScheduledPaymentsSections = () => {
    const data = paymentSchedules.map(transformToPropsByPaymentSchedulesTypes)
    if (!data.length) return [] as const
    return [
      {
        topLevelHeader: (
          <Header testID={CardPaymentsE2E.Ids.Text.ScheduledPaymentsHeader}>
            Scheduled Payments
          </Header>
        ),
        title: 'Pending',
        data,
      },
    ] as const satisfies SectionListData<ItemProps>[]
  }

  const generateCardActivitiesTransactionsSections = () => {
    const groupedAllCardActivitiesByDate = Object.groupBy(finalizedActivities, (cardActivity) =>
      new Date(cardActivity.sourceCreatedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
      }),
    )

    const pendingCardActivities = {
      topLevelHeader: <Header>Transactions</Header>,
      title: 'Pending',
      data: pendingActivities.map(transformToPropsByActivityType),
    } as const satisfies SectionListData<ItemProps>

    const finalizedCardActivities = Object.keys(groupedAllCardActivitiesByDate).map(
      (date) =>
        ({
          ...(pendingCardActivities.data.length > 0
            ? {}
            : { topLevelHeader: <Header>Transactions</Header> }),
          title: date,
          data: groupedAllCardActivitiesByDate[date]
            ? groupedAllCardActivitiesByDate[date].map(transformToPropsByActivityType)
            : [],
        }) as const satisfies SectionListData<ItemProps>,
    )

    if (pendingCardActivities.data.length > 0) {
      finalizedCardActivities.unshift(pendingCardActivities)
    }

    return finalizedCardActivities.filter((section) => section.data.length > 0)
  }

  // King: This actually differs from how we currently render the feed
  // Will keep this for now, until I sync with product on benefits of this approach
  // Which auto populates feed by type <> section if supported
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const generateCardActivitySections = CardActivityTypes.options
    .map((activityType) => {
      const cardActivitiesFiltered = finalizedActivities.filter(
        (activity) => activity.type === activityType,
      )

      const groupedFilteredCardActivitiesByDate = Object.groupBy(
        cardActivitiesFiltered,
        (cardActivity) =>
          new Date(cardActivity.sourceCreatedAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
          }),
      )

      const pendingCardActivities = {
        topLevelHeader: <Header>{`${upperFirst(lowerCase(activityType))}s`}</Header>,
        title: 'Pending',
        data: pendingActivities
          .filter((activity) => activity.type === activityType)
          .map(transformToPropsByActivityType),
      } as const

      const finalizedCardActivities = Object.keys(groupedFilteredCardActivitiesByDate).map(
        (date) =>
          ({
            ...(pendingCardActivities.data.length > 0
              ? {}
              : { topLevelHeader: <Header>{`${upperFirst(lowerCase(activityType))}s`}</Header> }),
            title: date,
            data: groupedFilteredCardActivitiesByDate[date]
              ? groupedFilteredCardActivitiesByDate[date].map(transformToPropsByActivityType)
              : [],
          }) as const,
      )

      if (activityType === 'TRANSACTION' || activityType === 'CARD_ACCOUNT_TRANSFER') {
        finalizedCardActivities.unshift(pendingCardActivities)
      }

      return finalizedCardActivities.filter((section) => section.data.length > 0)
    })
    .filter((section) => section.length > 0)
    .flat()

  const initializing =
    pendingActivitiesLoading || finalizedActivitiesLoading || paymentSchedulesLoading

  return (
    <FeedWithData
      {...props}
      bottomSheetSectionListProps={{
        refreshing:
          !initializing &&
          (finalizedActivitiesQuery.isFetching || pendingActivitiesQuery.isFetching),
        onRefresh() {
          onRefresh?.()
          void paymentSchedulesQuery.refetch()
          void pendingActivitiesQuery.refetch()
          void finalizedActivitiesQuery.refetch()
        },
        sections: initializing
          ? []
          : [
              ...generateCardActivitiesScheduledPaymentsSections(),
              ...generateCardActivitiesTransactionsSections(),
            ],
        keyExtractor(item) {
          return item.id
        },
        stickySectionHeadersEnabled:
          // the browser's implementation of sticky section headers is a little different such that we need the right combination of background colors and z-indices (in `CellRendererComponent`). i don't have enough time to fix this before the web GA launch
          Platform.OS !== 'web',
        onEndReachedThreshold: 0.1,
        renderSectionHeader({ section }) {
          const topLevelHeader = 'topLevelHeader' in section ? section.topLevelHeader : null
          return (
            <>
              {topLevelHeader}
              <Text
                style={[
                  {
                    backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
                    color: mesaColorNames['semantic/fg/subtle #898D8F'],
                    ...typography['heading/heading6'],
                    marginBottom: spacing.base - 10,
                  },
                  { paddingHorizontal: spacing['4'] },
                ]}
              >
                {section.title}
              </Text>
            </>
          )
        },
        renderItem({ item }) {
          return <Item {...item} />
        },
        contentContainerStyle: {
          /** some extra padding on the bottom to derisk the last row being covered by the bottom nav bar */
          paddingBottom: 88,
        },
        showsVerticalScrollIndicator: false,
        onEndReached() {
          if (!finalizedActivitiesQuery.isFetching && finalizedActivitiesQuery.hasNextPage) {
            void finalizedActivitiesQuery.fetchNextPage()
          }
        },
        ListEmptyComponent() {
          return initializing ? (
            <>
              <Skeleton
                style={{ paddingLeft: 16, marginBottom: 16, height: 21.602, width: 222 }}
                skeletonProps={{ height: 21.602 }}
              />
              <Skeleton
                style={{ paddingLeft: 16, marginBottom: 6, height: 14.398, width: 88 }}
                skeletonProps={{ height: 14.398 }}
              />
              {generateStubCardActivities()}
            </>
          ) : (
            <Empty
              cardActivityFeedBottomSheetHeightStore={cardActivityFeedBottomSheetHeightStore}
            />
          )
        },
        ListFooterComponent:
          !initializing &&
          (finalizedActivitiesQuery.isFetching ||
            pendingActivitiesQuery.isFetching ||
            paymentSchedulesQuery.isFetching) ? (
            <>{generateStubCardActivities(3)}</>
          ) : undefined,
      }}
    />
  )
}

function FeedWithoutCardAccount(props: ComponentProps<typeof Feed>) {
  return (
    <FeedWithData
      {...props}
      bottomSheetSectionListProps={{
        onRefresh: props.onRefresh,
        refreshing: false,
        sections: [],
        ListEmptyComponent: generateStubCardActivities,
      }}
    />
  )
}

function FeedWithData({
  children,
  cardActivityFeedBottomSheetHeightStore,
  bottomSheetSectionListProps,
}: ComponentProps<typeof Feed> & {
  bottomSheetSectionListProps: ComponentProps<typeof FeedBottomSheetSectionList>
}) {
  return (
    <>
      {children}
      <BottomSheet cardActivityFeedBottomSheetHeightStore={cardActivityFeedBottomSheetHeightStore}>
        <Animated.View style={cardActivityFeedBottomSheetHeightStore.animatedStyles}>
          <FeedBottomSheetSectionList {...bottomSheetSectionListProps} />
        </Animated.View>
      </BottomSheet>
    </>
  )
}
